import { useState, useCallback } from 'react'
import { useForm, type UseFormReturn, type RegisterOptions } from 'react-hook-form'

import { REVIEW_LABELS as LABELS } from '../lib'

import type { ReviewFormValues, ReviewRating } from '../types'

export interface ReviewFormFields {
  nickname: string
  summary: string
  text: string
}

export interface ReviewFormState {
  /** Current star rating (1-5, 0 = not selected) */
  rating: number
  /** Whether form was successfully submitted */
  formSubmitted: boolean
  /** Whether success modal is open */
  isModalOpen: boolean
  /** Key to force StarRatingInput remount on reset */
  starsKey: number
  /** Star rating validation error */
  starRatingError: string | null
  /** Submission error from API */
  submitError: string | null
}

export interface ReviewFormActions {
  /** Set star rating */
  setRating: (rating: number) => void
  /** Handle form submission */
  handleSubmit: (data: ReviewFormFields) => Promise<void>
  /** Close modal and reset form */
  handleCloseModal: () => void
  /** Clear star rating error */
  clearStarRatingError: () => void
}

export interface UseReviewFormOptions {
  /** Product SKU */
  productSku: string
  /** Rating metadata from API */
  neededRating: ReviewRating | undefined
  /** Submit callback from context (async) */
  submitReview: (review: ReviewFormValues) => Promise<void>
}

export interface UseReviewFormReturn {
  /** Form state */
  state: ReviewFormState
  /** Form actions */
  actions: ReviewFormActions
  /** React Hook Form instance */
  form: UseFormReturn<ReviewFormFields>
  /** Rating ID from API */
  ratingId: string | undefined
  /** Validation rules */
  validationRules: {
    nickname: RegisterOptions<ReviewFormFields, 'nickname'>
    summary: RegisterOptions<ReviewFormFields, 'summary'>
    text: RegisterOptions<ReviewFormFields, 'text'>
  }
}

/**
 * Hook for review form state and logic
 *
 * Manages all form state including star rating, modal, and validation.
 * Components should only render UI.
 *
 * Note: Does NOT import from state to avoid circular dependencies.
 * Required data is passed via options parameter.
 */
export function useReviewForm(options: UseReviewFormOptions): UseReviewFormReturn {
  const { productSku, neededRating, submitReview } = options
  const form = useForm<ReviewFormFields>()

  // Form state
  const [rating, setRatingState] = useState(0)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [starsKey, setStarsKey] = useState(0)
  const [starRatingError, setStarRatingError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const ratingId = neededRating?.id

  /**
   * Set star rating and clear error
   */
  const setRating = useCallback((newRating: number) => {
    setRatingState(newRating)
    setStarRatingError(null)
  }, [])

  /**
   * Clear star rating error
   */
  const clearStarRatingError = useCallback(() => {
    setStarRatingError(null)
  }, [])

  /**
   * Validate star rating
   */
  const validateRating = useCallback((): boolean => {
    if (rating === 0) {
      setStarRatingError(LABELS.ratingRequired)
      return false
    }
    setStarRatingError(null)
    return true
  }, [rating])

  /**
   * Handle form submission (async - waits for API response)
   */
  const handleSubmit = useCallback(
    async (data: ReviewFormFields) => {
      if (!validateRating()) return

      setSubmitError(null)

      const selectedValueId = neededRating?.values[rating - 1]?.value_id

      const reviewData: ReviewFormValues = {
        sku: productSku,
        nickname: data.nickname,
        summary: data.summary,
        text: data.text,
        ratings: [
          {
            id: ratingId || '',
            value_id: selectedValueId || '',
          },
        ],
      }

      try {
        await submitReview(reviewData)
        setFormSubmitted(true)
        setIsModalOpen(true)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'A aparut o eroare. Incearca din nou.'
        setSubmitError(message)
      }
    },
    [validateRating, neededRating, rating, productSku, ratingId, submitReview]
  )

  /**
   * Close modal and reset form for new submission
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setFormSubmitted(false)
    setRatingState(0)
    setStarsKey((prev) => prev + 1) // Force StarRatingInput remount
    setStarRatingError(null)
    setSubmitError(null)
    form.reset()
  }, [form])

  // Validation rules for form fields
  const validationRules = {
    nickname: {
      required: LABELS.nicknameRequired,
      maxLength: {
        value: 30,
        message: LABELS.nicknameMaxLength,
      },
    },
    summary: {
      required: LABELS.reviewTitleRequired,
      maxLength: {
        value: 50,
        message: LABELS.reviewTitleMaxLength,
      },
    },
    text: {
      maxLength: {
        value: 1000,
        message: LABELS.reviewTextMaxLength,
      },
    },
  }

  return {
    state: {
      rating,
      formSubmitted,
      isModalOpen,
      starsKey,
      starRatingError,
      submitError,
    },
    actions: {
      setRating,
      handleSubmit,
      handleCloseModal,
      clearStarRatingError,
    },
    form,
    ratingId,
    validationRules,
  }
}
